import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Wand2 } from "lucide-react";
import { toast } from "sonner";
import { CountryCityCombobox } from "./CountryCityCombobox";

interface AgentCode {
  id: string;
  code: string;
  agent_name: string;
  agent_email: string | null;
  agent_company: string | null;
  discount_percentage: number;
  commission_percentage: number | null;
  is_active: boolean;
  created_at: string;
  agent_type?: string;
  associated_hotel?: string | null;
  city?: string | null;
  country?: string | null;
}

interface CreateAgentCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingAgent?: AgentCode | null;
  clientId?: string;
  prefill?: {
    name?: string | null;
    email?: string | null;
    company?: string | null;
    country?: string | null;
    agentType?: 'AGENT' | 'CONCIERGE';
  };
}

const companyInitials = (company: string): string =>
  company
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, "")[0])
    .filter(Boolean)
    .join("")
    .toUpperCase();

const nameInitialSurname = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const clean = (s: string) => s.replace(/[^A-Za-z]/g, "");
  if (parts.length === 1) return clean(parts[0]).toUpperCase();
  const first = clean(parts[0])[0] ?? "";
  const surname = clean(parts[parts.length - 1]);
  return (first + surname).toUpperCase();
};

const buildAgentCode = (company: string, name: string, countryCode: string): string =>
  [companyInitials(company), nameInitialSurname(name), (countryCode || "").toUpperCase()]
    .filter((s) => s && s.length > 0)
    .join("_");

const fullNameConcat = (name: string): string =>
  name
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z]/g, ""))
    .filter(Boolean)
    .join("")
    .toUpperCase();

const hotelInitials = (hotel: string): string =>
  hotel
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, "")[0])
    .filter(Boolean)
    .join("")
    .toUpperCase();

const buildConciergeCode = (name: string, hotel: string): string =>
  ["CONCIERGE", fullNameConcat(name), hotelInitials(hotel)]
    .filter((s) => s && s.length > 0)
    .join("_");

export function CreateAgentCodeDialog({ open, onOpenChange, editingAgent, clientId, prefill }: CreateAgentCodeDialogProps) {
  const queryClient = useQueryClient();
  const isEditing = !!editingAgent;

  const [code, setCode] = useState("");
  const [agentName, setAgentName] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [agentCompany, setAgentCompany] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [commissionPercentage, setCommissionPercentage] = useState<string>("");
  const [isActive, setIsActive] = useState(true);
  const [agentType, setAgentType] = useState<'AGENT' | 'CONCIERGE'>('AGENT');
  const [associatedHotel, setAssociatedHotel] = useState("");
  const [city, setCity] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);

  const { data: countriesList = [] } = useQuery({
    queryKey: ["countries-with-code"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("countries")
        .select("name, code");
      if (error) throw error;
      return (data || []) as { name: string; code: string | null }[];
    },
  });

  // Reset form when dialog opens/closes or editing changes
  useEffect(() => {
    if (open) {
      if (editingAgent) {
        setCode(editingAgent.code);
        setCodeManuallyEdited(true);
        setAgentName(editingAgent.agent_name);
        setAgentEmail(editingAgent.agent_email || "");
        setAgentCompany(editingAgent.agent_company || "");
        setDiscountPercentage(editingAgent.discount_percentage);
        setCommissionPercentage(editingAgent.commission_percentage?.toString() || "");
        setIsActive(editingAgent.is_active);
        setAgentType((editingAgent.agent_type as 'AGENT' | 'CONCIERGE') || 'AGENT');
        setAssociatedHotel((editingAgent as any).associated_hotel || "");
        setCity((editingAgent as any).city || "");
        setCountry((editingAgent as any).country || "");
      } else {
        setCode("");
        setCodeManuallyEdited(false);
        setAgentName(prefill?.name || "");
        setAgentEmail(prefill?.email || "");
        setAgentCompany(prefill?.company || "");
        setDiscountPercentage(0);
        setCommissionPercentage("");
        setIsActive(true);
        setAgentType(prefill?.agentType || 'AGENT');
        setAssociatedHotel("");
        setCity("");
        setCountry(prefill?.country || "");
      }
    }
  }, [open, editingAgent]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        code: code.toUpperCase().trim(),
        agent_name: agentName.trim(),
        agent_email: agentEmail.trim() || null,
        agent_company: agentCompany.trim() || null,
        discount_percentage: agentType === 'CONCIERGE' ? 0 : discountPercentage,
        commission_percentage: commissionPercentage ? parseFloat(commissionPercentage) : null,
        is_active: isActive,
        agent_type: agentType,
        associated_hotel: associatedHotel.trim() || null,
        city: city.trim() || null,
        country: country.trim() || null,
        ...(clientId ? { client_id: clientId } : {}),
      };

      const { error } = await supabase
        .from("agent_codes")
        .insert(payload);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-codes"] });
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: ["client-agent-codes", clientId] });
      }
      toast.success("Agent code created successfully");
      onOpenChange(false);
    },
    onError: (error: any) => {
      if (error.message?.includes("duplicate")) {
        toast.error("An agent code with this code already exists");
      } else {
        toast.error("Failed to create agent code");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingAgent) return;

      const payload = {
        code: code.toUpperCase().trim(),
        agent_name: agentName.trim(),
        agent_email: agentEmail.trim() || null,
        agent_company: agentCompany.trim() || null,
        discount_percentage: agentType === 'CONCIERGE' ? 0 : discountPercentage,
        commission_percentage: commissionPercentage ? parseFloat(commissionPercentage) : null,
        is_active: isActive,
        updated_at: new Date().toISOString(),
        agent_type: agentType,
        associated_hotel: associatedHotel.trim() || null,
        city: city.trim() || null,
        country: country.trim() || null,
      };

      const { error } = await supabase
        .from("agent_codes")
        .update(payload)
        .eq("id", editingAgent.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-codes"] });
      toast.success("Agent code updated successfully");
      onOpenChange(false);
    },
    onError: (error: any) => {
      if (error.message?.includes("duplicate")) {
        toast.error("An agent code with this code already exists");
      } else {
        toast.error("Failed to update agent code");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      toast.error("Please enter a code");
      return;
    }
    
    if (!agentName.trim()) {
      toast.error("Please enter an agent name");
      return;
    }
    
    if (discountPercentage < 0 || discountPercentage > 100) {
      toast.error("Discount must be between 0 and 100");
      return;
    }

    if (isEditing) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const countryCode =
    countriesList.find((c) => c.name.toLowerCase() === country.trim().toLowerCase())?.code || "";

  // Auto-sync code from formula while creating, unless the user has overridden it
  useEffect(() => {
    if (isEditing || codeManuallyEdited) return;
    const next =
      agentType === "CONCIERGE"
        ? buildConciergeCode(agentName, associatedHotel || agentCompany)
        : buildAgentCode(agentCompany, agentName, countryCode);
    setCode(next);
  }, [
    agentType,
    agentCompany,
    agentName,
    associatedHotel,
    countryCode,
    isEditing,
    codeManuallyEdited,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Agent Code" : "Create Agent Code"}</DialogTitle>
          <DialogDescription>
            {isEditing 
              ? "Update the agent code details below"
              : "Create a discount code for a tourism trade partner"
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Agent Type Toggle */}
          <div className="space-y-2">
            <Label>Type *</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={agentType === 'AGENT' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAgentType('AGENT')}
                className="flex-1"
              >
                Agent
              </Button>
              <Button
                type="button"
                variant={agentType === 'CONCIERGE' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAgentType('CONCIERGE')}
                className="flex-1"
              >
                Concierge
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {agentType === 'AGENT' 
                ? 'Agent gets a discount applied at checkout (net pricing)' 
                : 'Guest pays full price. Concierge earns a referral fee.'}
            </p>
          </div>

          {/* Agent Name */}
          <div className="space-y-2">
            <Label htmlFor="agentName">{agentType === 'CONCIERGE' ? 'Concierge Name' : 'Agent Name'} *</Label>
            <Input
              id="agentName"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder={agentType === 'CONCIERGE' ? 'Raymond du Pont' : 'John Smith'}
            />
          </div>

          {/* Agent Email */}
          <div className="space-y-2">
            <Label htmlFor="agentEmail">{agentType === 'CONCIERGE' ? 'Concierge Email' : 'Agent Email'}</Label>
            <Input
              id="agentEmail"
              type="email"
              value={agentEmail}
              onChange={(e) => setAgentEmail(e.target.value)}
              placeholder="agent@company.com"
            />
          </div>

          {/* Agent Company */}
          <div className="space-y-2">
            <Label htmlFor="agentCompany">Company</Label>
            <Input
              id="agentCompany"
              value={agentCompany}
              onChange={(e) => setAgentCompany(e.target.value)}
              placeholder={agentType === 'CONCIERGE' ? 'Cape Grace Hotel' : 'ABC Travel Agency'}
            />
          </div>

          {/* Associated Hotel (Concierge only) */}
          {agentType === 'CONCIERGE' && (
            <div className="space-y-2">
              <Label htmlFor="associatedHotel">Associated Hotel / Property</Label>
              <Input
                id="associatedHotel"
                value={associatedHotel}
                onChange={(e) => setAssociatedHotel(e.target.value)}
                placeholder="Cape Grace Hotel"
              />
              <p className="text-xs text-muted-foreground">The hotel or property this concierge represents</p>
            </div>
          )}

          {/* Country & City cascade */}
          <div className="space-y-2">
            <CountryCityCombobox
              country={country}
              city={city}
              onCountryChange={setCountry}
              onCityChange={setCity}
              idPrefix="agent"
            />
            {agentType === 'CONCIERGE' && (
              <p className="text-xs text-muted-foreground">
                Pre-fills the city in the concierge booking widget for this concierge's guests.
              </p>
            )}
          </div>

          {/* Code (auto-generated from the fields above) */}
          <div className="space-y-2">
            <Label htmlFor="code">Code *</Label>
            <div className="flex gap-2">
              <Input
                id="code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setCodeManuallyEdited(true);
                }}
                placeholder={
                  agentType === "CONCIERGE"
                    ? "CONCIERGE_IAINHARRIS_KSH"
                    : "IT_PJOHNSON_ZA"
                }
                className="font-mono"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  const next =
                    agentType === "CONCIERGE"
                      ? buildConciergeCode(agentName, associatedHotel || agentCompany)
                      : buildAgentCode(agentCompany, agentName, countryCode);
                  setCode(next);
                  setCodeManuallyEdited(false);
                }}
                title="Regenerate code from the fields above"
              >
                <Wand2 className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {agentType === "CONCIERGE"
                ? "Auto-generated from Concierge name + Hotel initials (e.g. Kloof Street Hotel → KSH)."
                : "Auto-generated from Company initials + Agent name + Country code (e.g. IT_PJOHNSON_ZA)."}
            </p>
          </div>

          {/* Discount & Commission */}
          <div className="grid grid-cols-2 gap-4">
            {agentType === 'AGENT' && (
              <div className="space-y-2">
                <Label htmlFor="discount">Discount % *</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="discount"
                    type="number"
                    min={0}
                    max={100}
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(Number(e.target.value))}
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">Discount for guest</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="commission">{agentType === 'CONCIERGE' ? 'Referral Fee %' : 'Commission %'}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="commission"
                  type="number"
                  min={0}
                  max={50}
                  value={commissionPercentage}
                  onChange={(e) => setCommissionPercentage(e.target.value)}
                  placeholder={agentType === 'CONCIERGE' ? '20' : '0'}
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {agentType === 'CONCIERGE' ? 'Referral fee owed to concierge' : 'For agent tracking'}
              </p>
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">Active</Label>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : isEditing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
